/**
 * WAF Account Takeover prevention (Owl/Mlytics — AccountTakeoverService +
 * TemplatesService). Service-scoped, domain-anchored (`?domain=`), and
 * per-category (`?category=failed_login|credential_stuffing`).
 *
 * Backed by `modules/service/waf/controller_account_takeover.go`:
 *   - GET    /services/:service_id/waf/atp/templates       → security-template catalog (clone source)
 *   - GET    /services/:service_id/waf/atp/rules           → a domain's rules for a category
 *   - POST   /services/:service_id/waf/atp/rules           → create a rule
 *   - PATCH  /services/:service_id/waf/atp/rules/:id        → replace a rule (full body)
 *   - PATCH  /services/:service_id/waf/atp/rules/:id/enable → toggle a rule on/off
 *   - DELETE /services/:service_id/waf/atp/rules/:id        → delete a rule
 *
 * Rules are rule-based (action + match conditions), usually cloned from a
 * template. List endpoints return a plain array under `data` (not paginated).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

export type ATPCategory = 'failed_login' | 'credential_stuffing';

/** Rule action — `type` is `failedLogin`|`credentialStuffing`; the rest is category-specific. */
export interface ATPAction {
  type: string;
  // failed_login
  req_count?: number;
  fl_check_time?: number;
  fl_action?: string;
  fl_block_time?: number;
  surge_defend?: boolean;
  surge_defend_num?: number;
  surge_lock_time?: number;
  // credential_stuffing
  username_expression?: string;
  password_expression?: string;
  cc_action?: string;
}

/** One match condition (plain strings; templates prefill realistic values). */
export interface ATPCondition {
  field: string;
  key?: string;
  operator: string;
  value?: string;
}

/** A stored account-takeover rule (`GET /waf/atp/rules`). */
export interface AccountTakeoverRule {
  id: string;
  rule_id: number;
  name: string;
  enable: boolean;
  priority: number;
  type: string;
  action: ATPAction;
  conditions: ATPCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Create/update body (full replace on update). */
export interface AccountTakeoverRuleInput {
  name: string;
  enable?: boolean;
  action: ATPAction;
  conditions: ATPCondition[];
}

/** A security template — clone source with action + conditions ready. */
export interface SecurityTemplate {
  id: string;
  name: string;
  type: string;
  action: ATPAction;
  conditions: ATPCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Security-template catalog for a category (clone source). */
export function useWafSecurityTemplates(
  serviceId: string | undefined,
  category: ATPCategory | undefined,
) {
  return useQuery<SecurityTemplate[]>({
    queryKey: wafKeys.atpTemplates(serviceId ?? '', category ?? ''),
    enabled: !!serviceId && !!category,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<SecurityTemplate[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/atp/templates`, { params: { category } }),
      ) ?? [],
  });
}

/** A domain's account-takeover rules for a category. */
export function useWafATPRules(
  serviceId: string | undefined,
  domain: string | undefined,
  category: ATPCategory | undefined,
) {
  return useQuery<AccountTakeoverRule[]>({
    queryKey: wafKeys.atpRules(serviceId ?? '', domain ?? '', category ?? ''),
    enabled: !!serviceId && !!domain && !!category,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<AccountTakeoverRule[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/atp/rules`, { params: { domain, category } }),
      ) ?? [],
  });
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>, serviceId?: string) {
  if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'atp-rules'] });
}

/** Create an account-takeover rule (`POST /waf/atp/rules`). */
export function useCreateATPRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<AccountTakeoverRule, Error, { domain: string; category: ATPCategory; input: AccountTakeoverRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, category, input }: { domain: string; category: ATPCategory; input: AccountTakeoverRuleInput }) =>
      unwrap<AccountTakeoverRule>(
        await serviceApiV3.post(`/services/${serviceId}/waf/atp/rules`, input, { params: { domain, category } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Replace an account-takeover rule (`PATCH /waf/atp/rules/:id`). */
export function useUpdateATPRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<AccountTakeoverRule, Error, { domain: string; category: ATPCategory; id: string; input: AccountTakeoverRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, category, id, input }: { domain: string; category: ATPCategory; id: string; input: AccountTakeoverRuleInput }) =>
      unwrap<AccountTakeoverRule>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/atp/rules/${id}`, input, { params: { domain, category } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Toggle an account-takeover rule on/off (`PATCH /waf/atp/rules/:id/enable`). */
export function useSetATPRuleEnabled(
  serviceId: string | undefined,
  options?: UseMutationOptions<AccountTakeoverRule, Error, { domain: string; category: ATPCategory; id: string; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, category, id, enable }: { domain: string; category: ATPCategory; id: string; enable: boolean }) =>
      unwrap<AccountTakeoverRule>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/atp/rules/${id}/enable`, { enable }, { params: { domain, category } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Delete an account-takeover rule (`DELETE /waf/atp/rules/:id`). */
export function useDeleteATPRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { domain: string; category: ATPCategory; id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, category, id }: { domain: string; category: ATPCategory; id: string }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/atp/rules/${id}`, { params: { domain, category } });
    },
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}
